import { useEffect, useRef, useState } from "react";
import { createDetector, grab } from "../scan.ts";
import type { ScanEngine } from "../scan.ts";
import { WORDS } from "../brand.ts";

/** iPhone and iPad: Safari switches the lamp off again while the camera runs, so no lamp there. */
const IOS =
  /iPad|iPhone|iPod/.test(navigator.userAgent) ||
  (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);

/**
 * Camera overlay; calls onCode once with the first barcode read. The camera starts once per opening
 * (onCode in a ref): restarting it on every render made Safari ask for the camera again and again.
 * While typing, the picture steps aside so field and button stay above the keyboard.
 */
export function Scanner({
  onCode,
  onClose,
}: {
  onCode: (code: string) => void;
  onClose: () => void;
}) {
  const video = useRef<HTMLVideoElement>(null);
  const [engine, setEngine] = useState<ScanEngine | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [manual, setManual] = useState("");
  /** The camera's lamp, where the browser offers it (Chrome on Android does; whether Safari does depends on the iOS version). */
  const [typing, setTyping] = useState(false);
  const report = useRef(onCode);
  report.current = onCode;
  const [lamp, setLamp] = useState<{
    track: MediaStreamTrack;
    on: boolean;
  } | null>(null);

  useEffect(() => {
    let stream: MediaStream | null = null;
    let stop = false;
    const canvas = document.createElement("canvas");
    (async () => {
      try {
        const detector = await createDetector();
        setEngine(detector.engine);
        stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: "environment", width: { ideal: 1280 } },
          audio: false,
        });
        const track = stream.getVideoTracks()[0];
        const caps = (track?.getCapabilities?.() ?? {}) as { torch?: boolean };
        if (track && caps.torch && !IOS) setLamp({ track, on: false });
        const v = video.current!;
        v.srcObject = stream;
        await v.play();
        while (!stop) {
          const source = detector.engine === "zxing-wasm" ? grab(v, canvas) : v;
          const code = source
            ? await detector.detect(source).catch(() => null)
            : null;
          if (code && !stop) {
            stop = true;
            navigator.vibrate?.(60);
            report.current(code);
            return;
          }
          await new Promise((r) => setTimeout(r, 120));
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : String(err));
      }
    })();
    return () => {
      stop = true;
      for (const t of stream?.getTracks() ?? []) t.stop();
    };
  }, []);

  return (
    <div className={`scanner${typing ? " typing" : ""}`}>
      <video ref={video} playsInline muted />
      <div className="scanline" />
      <div className="scanbar">
        <span className="muted">
          {error
            ? `Kamera: ${error}`
            : engine
              ? `Leser: ${engine}`
              : "Kamera startet …"}
        </span>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (manual.trim()) onCode(manual.trim());
          }}
          className="scan-manual"
        >
          <input
            placeholder="Strichcode eintippen"
            inputMode="numeric"
            enterKeyHint="go"
            value={manual}
            onChange={(e) => setManual(e.target.value)}
            onFocus={() => setTyping(true)}
            onBlur={() => {
              setTyping(false);
              video.current?.play().catch(() => undefined);
            }}
            data-testid="scan-manual"
          />
          <button
            type="submit"
            disabled={!manual.trim()}
            onMouseDown={(e) => e.preventDefault()}
            data-testid="scan-manual-ok"
          >
            Übernehmen
          </button>
        </form>
        {lamp ? (
          <button
            onClick={async () => {
              const on = !lamp.on;
              await lamp.track
                .applyConstraints({
                  advanced: [{ torch: on } as MediaTrackConstraintSet],
                })
                .catch(() => undefined);
              setLamp({ ...lamp, on });
            }}
            data-testid="lamp"
          >
            {lamp.on ? "Lampe aus" : "Lampe an"}
          </button>
        ) : (
          !error && (
            <span className="muted small">
              {WORDS.scanHint}
            </span>
          )
        )}
        <button onClick={onClose}>Schließen</button>
      </div>
    </div>
  );
}
